import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { api } from "@/lib/api";
import { Section, CTAButton } from "@/components/shared";
import { BookingEmbed } from "@/components/BookingEmbed";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_ATTEMPTS = 6;

export default function PaymentReturn() {
  useSeo("Payment Status | Systems Beyond", "Payment status for your Systems Beyond subscription.");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("pending");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) setState("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  const content = {
    checking: { icon: <Loader2 size={40} className="text-acid-dark animate-spin" />, title: "Confirming your payment…", desc: "Please wait while we verify the transaction with Stripe." },
    paid: { icon: <CheckCircle2 size={40} className="text-acid-dark" />, title: "Payment confirmed.", desc: "Thank you for subscribing. We'll reach out shortly to begin your engagement and set the first monthly priorities." },
    pending: { icon: <Loader2 size={40} className="text-blue-gray" />, title: "Payment is processing.", desc: "This is taking longer than usual. You'll receive confirmation once the payment settles." },
    error: { icon: <XCircle size={40} className="text-dark-gray" />, title: "We couldn't confirm this payment.", desc: "If you were charged, please contact us and we'll resolve it promptly." },
  }[state];

  return (
    <Section className="pt-44 pb-32 min-h-screen" grid>
      <div className="max-w-xl border border-silver/50 p-10 lg:p-14 bg-off-white" data-testid="payment-return">
        <div className="mb-8">{content.icon}</div>
        <p className="overline">Payment status</p>
        <h1 className="mt-4 text-3xl lg:text-4xl tracking-tight font-medium text-ink" data-testid="payment-status-title">{content.title}</h1>
        <p className="mt-5 text-dark-gray leading-relaxed">{content.desc}</p>
        <div className="mt-10 flex flex-wrap gap-3">
          <CTAButton to="/" variant="accent" testId="payment-home">Back to Home</CTAButton>
          <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3.5 text-[13px] font-medium border border-silver hover:border-charcoal transition-all">Contact Us</Link>
        </div>
      </div>

      {state === "paid" && (
        <div className="mt-14 max-w-3xl">
          <BookingEmbed
            title="Schedule your onboarding call"
            subtitle="Pick a time that works for you. We'll align on scope, priorities and the first month of work."
          />
        </div>
      )}
    </Section>
  );
}
